import { Component, computed, effect, ElementRef, inject, input, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonButton, IonIcon, IonLabel, IonRippleEffect, IonSpinner, ModalController } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { add, checkmarkCircle, cloudUploadOutline, imageOutline, refreshCircle, remove } from 'ionicons/icons';
import { isEqual } from 'lodash-es';

import { NgClass } from '@angular/common';
import { FormControl, Validators } from '@angular/forms';
import { VxDropzoneComponent } from '@libs/core/lib/mtjr-dropzone.component';
import { AsyncState, successState } from '@libs/core/lib/state/async-states';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { Entity } from '@libs/mtjr/shared/generated/types';
import { FileInputDirective, FileInputValue } from '@ngx-dropzone/cdk';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { UploadService } from './upload_service';

@Component({
  selector: 'mtjr-attachment-field',
  templateUrl: './attachment-field.component.html',
  styles: [
    `
      ngx-dropzone,
      .custom-dropzone {
        height: 250px;
        background: var(--ion-background);
        border: 5px dashed var(--ion-color-primary);
        border-radius: 5px;
        font-size: 20px;
      }
    `,
  ],
  imports: [
    IonLabel,
    IonButton,
    IonIcon,
    IonSpinner,
    VxDropzoneComponent,
    FileInputDirective,
    TranslatePipe,
    IonRippleEffect,
    NgClass,
  ],
})
export class VxAttachmentFieldComponent {
  // Input properties
  singleMode = input<boolean>(false);
  field = input.required<FormControl<string | string[] | undefined | null>>();
  showLabel = input<boolean>(true);
  labelTr = input<string>('PRODUCTS.ADD_IMAGE');
  borderRadiusClass = input<string>('!rounded-xl');

  // Internal signals
  isDragging = signal(false);
  uploadEntries = signal<UploadEntry[]>([]);
  singleEntry = computed(() => {
    if (this.singleMode() && this.uploadEntries().length === 1) {
      return this.uploadEntries()[0];
    }
    return null;
  });

  // Injected services
  modalCtr = inject(ModalController);
  alerts = inject(AppAlerts);
  vendorId = inject(ActivatedRoute).snapshot.params['vendorId'];
  uploadService = inject(UploadService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  elmRef = inject(ElementRef);

  initialValue: string | string[] | undefined;

  constructor() {
    addIcons({
      remove,
      add,
      imageOutline,
      refreshCircle,
      cloudUploadOutline,
      checkmarkCircle,
    });

    effect(() => {
      const field = this.field();
      if (!field) return;

      if (field.value) {
        this.populateInitialValue(field.value);
      } else {
        untracked(() => {
          field.valueChanges
            .pipe(
              filter((value) => value !== null && value !== undefined),
              take(1),
            )
            .subscribe((initial) => {
              this.populateInitialValue(initial);
            });
        });
      }
    });

    effect(() => {
      const entries = this.uploadEntries();
      const field = this.field();
      if (!field) return;
      const successEntries = entries.filter((entry) => entry.state.success);
      if (successEntries.length > 0) {
        field.setValue(this.singleMode() ? successEntries[0].url : successEntries.map((e) => e.url), {
          emitEvent: false,
        });
        if (!isEqual(field.value, this.initialValue)) {
          // console.log('Field is dirty', field.value, this.initialValue);
          field.markAsDirty();
        } else {
          // console.log('Field is pristine', field.value, this.initialValue);
          field.markAsPristine();
        }
      }

      const isRequired = field.hasValidator(Validators.required);

      if (entries.length === 0) {
        if (this.singleMode()) {
          field.setValue(undefined, { emitEvent: false });
        } else {
          field.setValue(isRequired ? [] : undefined, { emitEvent: false });
        }
        if (!isEqual(field.value, this.initialValue)) {
          // console.log('Field is dirty', field.value, this.initialValue);
          field.markAsDirty();
        } else {
          // console.log('Field is pristine', field.value, this.initialValue);
          field.markAsPristine();
        }
      }
    });
  }

  private populateInitialValue(initial: string | string[] | undefined) {
    this.initialValue = initial;
    let initialUrls: string[] = [];
    if (typeof initial === 'string') {
      initialUrls = [initial];
    } else if (Array.isArray(initial)) {
      initialUrls = initial;
    }

    if (initialUrls.length > 0) {
      this.uploadEntries.set(
        initialUrls.map((url) => ({
          state: successState<string>(url),
          url,
          label: url.split('/').pop() || 'File',
        })),
      );
    }
  }

  onDelete(entry: UploadEntry): void {
    const currentUrls = this.uploadEntries();
    if (currentUrls.includes(entry)) {
      this.uploadEntries.set(currentUrls.filter((url: UploadEntry) => url.url !== entry.url));
    }
  }

  onFilesPicked($event: FileInputValue): void {
    this.isDragging.set(false);
    if ($event === null) {
      console.log('[AttachmentField] Event is null, returning');
      return;
    }
    const files = Array.isArray($event) ? $event : [$event];

    if (this.singleMode() && files.length > 0) {
      if (this.singleEntry()?.file?.name === files[0].name) {
        this.alerts.snackbarTR('ATTACHMENTS.FILE_ALREADY_EXISTS');
        return;
      }
      this.uploadEntries.set([]);
    }

    for (const file of files) {
      const fileUrl = URL.createObjectURL(file);
      if (!this.uploadEntries().some((e) => e.file?.name === file.name)) {
        this._upload(file, fileUrl);
      } else {
        this.alerts.snackbarTR('ATTACHMENTS.FILE_ALREADY_EXISTS');
      }
    }
  }

  private _upload(file: File, fileUrl: string): void {
    this.uploadService
      .upload(file, { key: fileUrl, vendorId: this.vendorId })
      .subscribe(async (uploadResultState: AsyncState<Entity.Attachment>) => {
        this.uploadEntries.update((uploadEntries: UploadEntry[]) => {
          const existingIndex = uploadEntries.findIndex((e) => e.url === fileUrl);

          const urlResult = this.mapState(uploadResultState);

          if (existingIndex !== -1) {
            uploadEntries[existingIndex].state = urlResult;
            if (uploadResultState.success) {
              uploadEntries[existingIndex].url = uploadResultState.data.url;
            }
          } else {
            uploadEntries.push({ state: urlResult, url: fileUrl, file, label: file.name });
          }
          return [...uploadEntries];
        });
      });
  }

  mapState(state: AsyncState<Entity.Attachment>): AsyncState<string> {
    if (state.success) {
      return successState(state.data.url);
    }
    return state as AsyncState<string>;
  }

  remove(entry: UploadEntry): void {
    this.uploadEntries.update((cs) => {
      return cs.filter((e) => e.url !== entry.url);
    });
  }

  cancel(entry: UploadEntry): void {
    this.uploadService.cancel(entry.url);
    this.remove(entry);
  }

  retry(entry: UploadEntry): void {
    const file = entry.file;
    if (!file) {
      throw new Error('File not found');
    }
    this._upload(file, entry.url);
  }

  onDragLeave(): void {
    this.isDragging.set(false);
  }

  onDragOver(): void {
    this.isDragging.set(true);
  }

  isUploading(): boolean {
    return this.uploadEntries().some((entry) => entry.state.type === 'loading');
  }

  hasPendingUploads(): boolean {
    return this.uploadEntries().some((entry) => !entry.state.success);
  }
}

export type UploadEntry = {
  state: AsyncState<string>;
  url: string;
  label: string;
  file?: File;
};
